import styled from "@emotion/styled";
import csv from "csvtojson";
import { useRouter } from "next/router";
import React, { useRef } from "react";
import { toast } from "react-toastify";
import { useRecoilValue, useSetRecoilState } from "recoil";
import ImportContactImage from "../../../assets/images/import-contact";
import { DataFromCsvAtom } from "../../../atoms/DataFromCsvAtom";
import { TestDriveBannerAtom } from "../../../atoms/TestDriveBannerAtom";
import StyledButton from "../../StyledButton";
import { useModal } from "../Modal";

const Container = styled.div`
  width: 300px;
  height: 300px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const PrimaryText = styled.div`
  color: #2f2f2f;
  letter-spacing: 0.2px;
  font-weight: medium;
  font-size: 20px;
  margin: 16px 0px;
`;

const SecondaryText = styled.div`
  color: #979797;
  letter-spacing: 0.2px;
  font-weight: normal;
  width: 324px;
  text-align: center;
  font-size: 16px;
  margin-bottom: 30px;
`;

interface Props {}

export default function ImportContact(props: Props) {
  const csvInputRef = useRef<HTMLInputElement>(null);
  const setDataFromCsv = useSetRecoilState(DataFromCsvAtom);
  const modal = useModal();
  const router = useRouter();
  const { show: isTestDrive } = useRecoilValue(TestDriveBannerAtom);
  function setLocation(location: string) {
    router.push(location);
  }

  function handleFiles(e: any) {
    const file = e.target.files[0];

    if (window.FileReader) {
      const reader = new FileReader();
      reader.readAsText(file);
      reader.onload = async (evt: any) => {
        // 10MB limit
        if (file.size > 10000000) {
          toast("File is too large", { type: "error" });
          modal.popModal();
          return;
        }

        const contactsFromCsv = await csv().fromString(evt.target.result);

        if (!contactsFromCsv) {
          toast("Invalid file. Please try again with another file.", {
            type: "error",
          });

          modal.popModal();
          return;
        }

        if (contactsFromCsv.length === 0) {
          toast(
            "This file contains incomplete data. Please choose a new file and try again.",
            { type: "error" }
          );

          modal.popModal();
          return;
        }

        if (isTestDrive && contactsFromCsv.length > 100) {
          toast("Your test drive account is limited to one hundred contacts.", {
            type: "error",
          });

          modal.popModal();
          return;
        }

        setDataFromCsv(contactsFromCsv);
        setLocation("/contacts/import-emails");
        modal.popModal();
      };
      reader.onerror = (evt: any) => {
        if (evt.target.error.name === "NotReadableError") {
          toast("Canno't read file!", { type: "error" });
        }
      };
    } else {
      toast("FileReader are not supported in this browser.", { type: "error" });
    }
  }

  return (
    <Container>
      <ImportContactImage />
      <PrimaryText>Import Contacts</PrimaryText>
      <SecondaryText>
        Upload a .csv file with the contacts you would like to import. Upload
        limit 10MB.
      </SecondaryText>
      <StyledButton onClick={() => csvInputRef.current?.click()}>
        Choose a file
      </StyledButton>
      <input
        ref={csvInputRef}
        type="file"
        accept=".csv"
        onChange={handleFiles}
        style={{ display: "none" }}
      />
    </Container>
  );
}
